<template>
  <div class="mini-tab-content">
    <div
      v-if="loadingVitals"
      class="interc-loading"
    >
      <b-spinner
        small
        variant="primary"
      />
      <span>{{ $t('involvedPanel.vitalSigns.loading.message') }}</span>
    </div>
    <template v-else>
      <div class="interc-toolbar">
        <span class="interc-toolbar__title">
          <feather-icon
            icon="ActivityIcon"
            size="13"
            class="mr-25 text-primary"
          />
          {{ assessmentCountText }}
        </span>
        <button
          v-if="!readonly"
          class="btn btn-sm btn-primary"
          @click="showVitalModal = true"
        >
          <feather-icon
            icon="PlusIcon"
            size="13"
            class="mr-25"
          />
          {{ $t('involvedPanel.vitalSigns.toolbar.newButton') }}
        </button>
      </div>

      <div
        v-if="!vitalSigns.length"
        class="interc-empty"
      >
        <feather-icon
          icon="HeartIcon"
          size="26"
        />
        <p>{{ $t('involvedPanel.vitalSigns.empty.message') }}</p>
      </div>

      <div
        v-if="vitalSigns.length"
        class="vs-history"
      >
        <div class="table-responsive">
          <table class="table table-sm vs-history__table mb-0">
            <thead>
              <tr>
                <th>{{ $t('involvedPanel.vitalSigns.table.dateTime') }}</th>
                <th>{{ $t('involvedPanel.vitalSigns.table.heartRate') }}</th>
                <th>{{ $t('involvedPanel.vitalSigns.table.respiratoryRate') }}</th>
                <th>{{ $t('involvedPanel.vitalSigns.table.saturation') }}</th>
                <th>{{ $t('involvedPanel.vitalSigns.table.temperature') }}</th>
                <th>{{ $t('involvedPanel.vitalSigns.table.bloodPressure') }}</th>
                <th>{{ $t('involvedPanel.vitalSigns.table.glasgow') }}</th>
                <th>{{ $t('involvedPanel.vitalSigns.table.observations') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="vs in vitalSigns"
                :key="vs.id"
              >
                <td class="text-nowrap">
                  {{ vs.created_at | formatDate }}
                </td>
                <td>{{ vs.fc || '—' }}</td>
                <td>{{ vs.fr || '—' }}</td>
                <td>{{ vs.saturation ? vs.saturation + '%' : '—' }}</td>
                <td>{{ vs.temperature ? vs.temperature + '°C' : '—' }}</td>
                <td class="text-nowrap">
                  <span v-if="vs.pa_systolic">{{ vs.pa_systolic }}/{{ vs.pa_diastolic }}</span>
                  <span v-else>—</span>
                </td>
                <td>{{ vs.glasgow || '—' }}</td>
                <td class="vs-obs-cell">
                  {{ vs.other_observations || '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <b-modal
      v-model="showVitalModal"
      :title="$t('involvedPanel.vitalSigns.modal.title')"
      size="lg"
      centered
      no-close-on-backdrop
      @hidden="newVital = emptyVital()"
    >
      <div class="row">
        <div class="col-md-3 col-sm-6">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.heartRate') }}</label>
            <input
              v-model="newVital.fc"
              class="form-control"
              type="number"
              :placeholder="$t('involvedPanel.vitalSigns.modal.placeholder')"
            >
          </div>
        </div>
        <div class="col-md-3 col-sm-6">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.respiratoryRate') }}</label>
            <input
              v-model="newVital.fr"
              class="form-control"
              type="number"
              :placeholder="$t('involvedPanel.vitalSigns.modal.placeholder')"
            >
          </div>
        </div>
        <div class="col-md-3 col-sm-6">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.saturation') }}</label>
            <input
              v-model="newVital.saturation"
              class="form-control"
              type="number"
              :placeholder="$t('involvedPanel.vitalSigns.modal.placeholder')"
            >
          </div>
        </div>
        <div class="col-md-3 col-sm-6">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.temperature') }}</label>
            <input
              v-model="newVital.temperature"
              class="form-control"
              type="number"
              step="0.1"
              :placeholder="$t('involvedPanel.vitalSigns.modal.placeholder')"
            >
          </div>
        </div>
        <div class="col-md-3 col-sm-6">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.systolicBP') }}</label>
            <input
              v-model="newVital.pa_systolic"
              class="form-control"
              type="number"
              :placeholder="$t('involvedPanel.vitalSigns.modal.placeholder')"
            >
          </div>
        </div>
        <div class="col-md-3 col-sm-6">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.diastolicBP') }}</label>
            <input
              v-model="newVital.pa_diastolic"
              class="form-control"
              type="number"
              :placeholder="$t('involvedPanel.vitalSigns.modal.placeholder')"
            >
          </div>
        </div>
        <div class="col-md-3 col-sm-6">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.glasgow') }}</label>
            <input
              v-model="newVital.glasgow"
              class="form-control"
              type="number"
              min="3"
              max="15"
              :placeholder="$t('involvedPanel.vitalSigns.modal.placeholder')"
            >
          </div>
        </div>
        <div class="col-md-3 col-sm-6">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.glucose') }}</label>
            <input
              v-model="newVital.blood_glucose"
              class="form-control"
              type="number"
              :placeholder="$t('involvedPanel.vitalSigns.modal.placeholder')"
            >
          </div>
        </div>
        <div class="col-md-3 col-sm-6">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.weight') }}</label>
            <input
              v-model="newVital.weight"
              class="form-control"
              type="number"
              step="0.1"
              :placeholder="$t('involvedPanel.vitalSigns.modal.placeholder')"
            >
          </div>
        </div>
        <div class="col-md-3 col-sm-6">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.height') }}</label>
            <input
              v-model="newVital.height"
              class="form-control"
              type="number"
              step="0.01"
              :placeholder="$t('involvedPanel.vitalSigns.modal.placeholder')"
            >
          </div>
        </div>
        <div class="col-md-3 col-sm-6">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.consciousness') }}</label>
            <select
              v-model="newVital.neurological_status"
              class="form-control"
            >
              <option value="">
                {{ $t('involvedPanel.vitalSigns.modal.options.consciousness.empty') }}
              </option>
              <option :value="'Alerta'">
                {{ $t('involvedPanel.vitalSigns.modal.options.consciousness.alert') }}
              </option>
              <option :value="'Voz'">
                {{ $t('involvedPanel.vitalSigns.modal.options.consciousness.voice') }}
              </option>
              <option :value="'Dor'">
                {{ $t('involvedPanel.vitalSigns.modal.options.consciousness.pain') }}
              </option>
              <option :value="'Inconsciente'">
                {{ $t('involvedPanel.vitalSigns.modal.options.consciousness.unconscious') }}
              </option>
            </select>
          </div>
        </div>
        <div class="col-md-3 col-sm-6">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.airway') }}</label>
            <select
              v-model="newVital.airway"
              class="form-control"
            >
              <option value="">
                {{ $t('involvedPanel.vitalSigns.modal.options.airway.empty') }}
              </option>
              <option :value="'Pérvea'">
                {{ $t('involvedPanel.vitalSigns.modal.options.airway.patent') }}
              </option>
              <option :value="'Obstruída'">
                {{ $t('involvedPanel.vitalSigns.modal.options.airway.obstructed') }}
              </option>
              <option :value="'IOT'">
                {{ $t('involvedPanel.vitalSigns.modal.options.airway.iot') }}
              </option>
              <option :value="'MLS'">
                {{ $t('involvedPanel.vitalSigns.modal.options.airway.mls') }}
              </option>
            </select>
          </div>
        </div>
        <div class="col-md-3 col-sm-6">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.ventilation') }}</label>
            <select
              v-model="newVital.mechanical_ventilation"
              class="form-control"
            >
              <option value="">
                {{ $t('involvedPanel.vitalSigns.modal.options.ventilation.empty') }}
              </option>
              <option :value="'Espontânea'">
                {{ $t('involvedPanel.vitalSigns.modal.options.ventilation.spontaneous') }}
              </option>
              <option :value="'Assistida'">
                {{ $t('involvedPanel.vitalSigns.modal.options.ventilation.assisted') }}
              </option>
              <option :value="'Controlada'">
                {{ $t('involvedPanel.vitalSigns.modal.options.ventilation.controlled') }}
              </option>
            </select>
          </div>
        </div>
        <div class="col-md-3 col-sm-6">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.oxygen') }}</label>
            <select
              v-model="newVital.support_o2"
              class="form-control"
            >
              <option value="">
                {{ $t('involvedPanel.vitalSigns.modal.options.oxygen.empty') }}
              </option>
              <option :value="'Sem O₂'">
                {{ $t('involvedPanel.vitalSigns.modal.options.oxygen.no') }}
              </option>
              <option :value="'Cateter nasal'">
                {{ $t('involvedPanel.vitalSigns.modal.options.oxygen.nasal') }}
              </option>
              <option :value="'Máscara simples'">
                {{ $t('involvedPanel.vitalSigns.modal.options.oxygen.simpleMask') }}
              </option>
              <option :value="'Máscara reservatório'">
                {{ $t('involvedPanel.vitalSigns.modal.options.oxygen.reservoirMask') }}
              </option>
              <option :value="'VMI'">
                {{ $t('involvedPanel.vitalSigns.modal.options.oxygen.vmi') }}
              </option>
            </select>
          </div>
        </div>
        <div class="col-md-6 col-sm-12">
          <div class="form-group">
            <label class="form-label">{{ $t('involvedPanel.vitalSigns.modal.fields.observations') }}</label>
            <textarea
              v-model="newVital.other_observations"
              class="form-control"
              rows="2"
              :placeholder="$t('involvedPanel.vitalSigns.modal.placeholder')"
            />
          </div>
        </div>
      </div>

      <template #modal-footer>
        <button
          class="btn btn-sm btn-outline-secondary mr-1"
          @click="showVitalModal = false"
        >
          {{ $t('involvedPanel.vitalSigns.modal.buttons.cancel') }}
        </button>
        <button
          class="btn btn-sm btn-success"
          :disabled="savingVital"
          @click="saveVital"
        >
          <b-spinner
            v-if="savingVital"
            small
            class="mr-25"
          />
          <feather-icon
            v-else
            icon="SaveIcon"
            size="13"
            class="mr-25"
          />
          {{ $t('involvedPanel.vitalSigns.modal.buttons.save') }}
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import involvedPanelMixin from './involvedPanelMixin'

export default {
  name: 'VitalSignsPanel',
  mixins: [involvedPanelMixin],
  data() {
    return {
      vitalSigns: [],
      loadingVitals: false,
      showVitalModal: false,
      savingVital: false,
      newVital: this.emptyVital(),
    }
  },
  computed: {
    assessmentCountText() {
      if (this.vitalSigns.length) {
        return this.$t(
          'involvedPanel.vitalSigns.toolbar.assessmentCount',
          { count: this.vitalSigns.length },
        )
      }
      return this.$t('involvedPanel.vitalSigns.toolbar.noAssessments')
    },
  },
  mounted() {
    this.fetchVitalSigns()
  },
  methods: {
    // Chamado pelo mixin ao reativar a aba (keep-alive): evita dados velhos sem F5.
    refresh() {
      return this.fetchVitalSigns()
    },

    emptyVital() {
      return {
        fc: '',
        fr: '',
        saturation: '',
        temperature: '',
        pa_systolic: '',
        pa_diastolic: '',
        glasgow: '',
        blood_glucose: '',
        weight: '',
        height: '',
        neurological_status: '',
        airway: '',
        mechanical_ventilation: '',
        support_o2: '',
        other_observations: '',
      }
    },
    async fetchVitalSigns() {
      this.loadingVitals = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}vital_signs_involveds`, {
          params: { involved_id: this.involvedId, occurrence_id: this.occurrenceId },
        })
        this.vitalSigns = res.data.data || res.data || []
      } catch (e) {
        this.vitalSigns = []
      } finally {
        this.loadingVitals = false
      }
    },
    async saveVital() {
      this.savingVital = true
      try {
        const payload = { ...this.newVital, involved_id: this.involvedId, occurrence_id: this.occurrenceId }
        const res = await this.$http.post(`${this.$store.state.api}vital_signs_involveds`, payload)
        const saved = res.data.data || res.data
        this.vitalSigns.push(saved)
        this.newVital = this.emptyVital()
        this.showVitalModal = false
        this.$message(
          this.$t('involvedPanel.vitalSigns.messages.saveTitle'),
          this.$t('involvedPanel.vitalSigns.messages.saveMessage'),
          'success',
        )
      } catch (e) {
        this.$message(
          this.$t('involvedPanel.vitalSigns.messages.errorTitle'),
          this.$t('involvedPanel.vitalSigns.messages.errorMessage'),
          'error',
        )
      } finally {
        this.savingVital = false
      }
    },
  },
}
</script>
